import { AxiosRequestConfig, CancelTokenSource, Method } from 'axios';
import { CacheProperties } from 'axios-cache-interceptor/src/cache/cache';

export type BearerFunction = (a: boolean) => string | undefined;
export type ExtensionalAccessToken = 'clarity' | 'fidentity';
export type AuthStatus = 'Allow' | 'Auth';
export type ResultType =
    | 'PasswordPolicyValidationFailed'
    | 'Success'
    | 'AccessDenied'
    | 'AccessTokenInvalid'
    | 'ActivationKeyInvalid'
    | 'ApplicationNotEnrolled'
    | 'ApplicationMultipleEnrolled'
    | 'AuditContextIdInvalid'
    | 'ConfirmPasswordInvalid'
    | 'ConfirmPasswordNotMatched'
    | 'ContactExternalInvalid'
    | 'ContactIdInvalid'
    | 'ContactLocked'
    | 'ContactAlreadyUnlocked'
    | 'ContactNotActivated'
    | 'ContactNotPasswordSet'
    | 'CredentialsInvalid'
    | 'DeviceAlreadyEnrolled'
    | 'DeviceMultipleEnrolled'
    | 'DeviceNotEnrolled'
    | 'EmailInvalid'
    | 'EmailMultipleContacts'
    | 'EmailNotSet'
    | 'JwtTokenExpired'
    | 'JwtTokenInvalid'
    | 'MemberIdInvalid'
    | 'MobileInvalid'
    | 'MobileMultipleContacts'
    | 'MTanInvalid'
    | 'PasscodeInvalid'
    | 'PasswordSetActive'
    | 'PasswordInvalid'
    | 'PasswordSameAlreadySet'
    | 'SessionAlreadyActive'
    | 'SessionExpired'
    | 'SessionInvalid'
    | 'TwoFactorDisabled'
    | 'UsernameInvalid'
    | 'UsernameAndEmailInvalid'
    | 'Waiting';

export interface SetupData {
    member_token?: string;
    notification_hub_token?: string;
    application_token?: string;
    application_generic_token?: string;
    withBearerApplicationAccessToken: boolean;
    bearerAccessToken?: string;
    bearerApplicationAccessToken?: string;
    bearerFIdentityAccessToken?: string;
    headers: Record<string, string>;
    isVersionIncluded: boolean;
    SERVICES_BASIC_AUTH?: string;
    DMS_BASIC_AUTH?: string;
    GATEWAY_API_URL: string;
    GRAPHQL_URL_SUFFIX: string;
    SERVICES_URL_SUFFIX: string;
    DMS_URL_SUFFIX: string;
    CUSTOM_URL_SUFFIX: string;
    DMS_URL?: string;
    GRAPHQL_URL?: string;
    SERVICES_URL?: string;
    CUSTOM_URL?: string;
    ENGAGEMENT_CONTENT_URL?: string;
    ENGAGEMENT_CONTENT_URL_SUFFIX: string;
    FIDENTITY_URL?: string;
    FIDENTITY_URL_SUFFIX: string;
    CLIENT_SERVICE_URL_SUFFIX: string;
    CSL_URL_SUFFIX: string;
    WMS_SERVICE_URL_SUFFIX: string;
    CLIENT_SERVICE_HEADERS: Record<string, string>;
    CUSTOM_SERVICE_LAYER_HEADERS: Record<string, string>;
    CLIENT_SERVICE_MEMBER_URL_SUFFIX: string;
    IFRAME_CASE_MANAGEMENT_URL: string;
    BO_ADDITIV_URL: string;
    BO_ZIAG_URL: string;
    CREDIT_APP_URL: string;
    CREDIT_APP_ZIAG_URL?: string;
    SYSTEM_MESSAGE_FEATURE_FLAG?: boolean;
    CREDIT_INTEGRATION_FEATURE_FLAG?: boolean;
    UPLOAD_STATIC_DOCUMENT_FEATURE_FLAG?: boolean;
    PRODUCT_PROPOSAL_FEATURE_FLAG?: boolean;
    CREDITSERVICELAYER_SERVICE_URL_SUFFIX?: string;
}

export enum APIVersion {
    V1 = 'v1',
    V10 = 'v1.0',
    V11 = 'v1.1',
    V12 = 'v1.2',
    V20 = 'v2.0',
    V21 = 'v2.1',
    V22 = 'v2.2',
    V23 = 'v2.3',
    V24 = 'v2.4',
    V25 = 'v2.5',
}

export type BearerType = 'APPLICATION' | 'AUTHORISATION';

export interface CancelTokenSourceWithMethod extends CancelTokenSource {
    method: string;
}

export enum ServiceTypes {
    DFS = 'DFS',
    DMS = 'DMS',
    FIDENTITY = 'FIDENTITY',

    WITH_APP_TOKEN = 'WITH_APP_TOKEN',
    WITH_MEMBER_TOKEN = 'WITH_MEMBER_TOKEN',
    WITH_GENERIC_TOKEN = 'WITH_GENERIC_TOKEN',
}

export interface RequestOptions {
    isSecure?: boolean;
    type?: ServiceTypes;
    axiosSetup?: AxiosRequestConfig;
    isAutoCancel?: boolean;
    withCancel?: boolean;
    bearerType?: BearerType;
    cache?: false | true | Partial<CacheProperties<any, any>>;
}

export interface RequestCoreParams<TData> {
    url: string;
    data?: TData;
    method: Method;
}

export interface SimpleRequestParameters {
    config?: RequestOptions;
}

export interface PayloadRequestParameters<Payload>
    extends SimpleRequestParameters {
    payload: Payload;
    access_token?: string;
}

export interface URLRequestParameters<URLParameters>
    extends SimpleRequestParameters {
    urlParams: URLParameters;
}

export interface URLWithPayloadRequestParameters<URLParameters, Payload>
    extends SimpleRequestParameters {
    payload: Payload;
    urlParams: URLParameters;
}
